import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Home, Lock, Calendar, HelpCircle, Search, X } from 'lucide-mui';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import type { ListProps } from '@mui/material';
import type { ActionType, URLParamsType } from '../../../types';
import type { RootState } from '../../../reducers';
import { getFolderUrl } from '../../../lib/routingUtils';
import FreeTrial from '../../plan/upgrade/FreeTrial';
import { useTags } from '../../../hooks/useTags';
import { MultiAskAIContext } from '../index';
import { setDrawerState } from '../dashboardSlice';

interface DrawerActionType extends ActionType<DrawerActionKeyType> {
  tooltip: string;
}

type DrawerActionKeyType = 'recents' | 'private';

const drawerActions: DrawerActionType[] = [
  {
    key: 'recents',
    label: 'Home',
    startIcon: <Home />,
    tooltip: 'Home',
  },
  {
    key: 'private',
    label: 'My Recordings',
    startIcon: <Lock />,
    tooltip: 'Your Personal Private Recordings',
  },
];

export const DrawerActions = (listProps: ListProps) => {
  const [showTrialModal, setShowTrialModal] = useState(false);
  const { setShowMultiAskAI, showMultiAskAI } = React.useContext(
    MultiAskAIContext
  );
  const {
    dashboard,
    team,
    plan,
    settings: {
      user: { _id: userId, createdAt },
    },
  } = useSelector((state: RootState) => state);
  const { getTag, setTag } = useTags();
  const { folderId } = useParams<URLParamsType>();
  const { selectedTeam, teams } = team;
  const teamId = selectedTeam?.teamId || (teams[0]?.teamId as string);
  const open = dashboard.drawerState === 'open';
  const { subscription } = plan;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const isFreePlan =
    !subscription?.package || subscription.package === 'FREE_LOGGEDIN';

  const userGoal = getTag('userGoal');
  const hasBusinessGoal = userGoal === 'business';

  const isTrialAvailable =
    isFreePlan &&
    !hasBusinessGoal &&
    userId === selectedTeam?.ownerId &&
    // TEMP OVERRIDE: Disable trial for everyone
    false &&
    // 1/3 chance to show trial button
    Number.parseInt(userId.slice(-6), 16) % 3 === 0 &&
    // Check if account created within last 24 hours
    new Date().getTime() - new Date(createdAt).getTime() < 24 * 60 * 60 * 1000;

  const handleQuickActionClick = () => {
    // Set default view and sorting preferences using tags
    setTag({ key: 'viewMode', value: 'grid' });
    setTag({ key: 'sortBy', value: 'createdAt' });
    setTag({ key: 'sortOrder', value: 'desc' });

    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  const actions: DrawerActionType[] = drawerActions.map((action) => ({
    ...action,
    href: getFolderUrl({
      teamId,
      folderId: action.key,
      withBasePath: false,
    }),
  }));

  // Add state to track if Ask AI panel is open
  const [isAskAIOpen, setIsAskAIOpen] = useState(false);

  // Update the state when MultiAskAI visibility changes
  React.useEffect(() => {
    setIsAskAIOpen(showMultiAskAI);
  }, [showMultiAskAI]);

  return (
    <>
      <List
        {...listProps}
        sx={{
          display: 'block',
          pt: 0.5,
          pb: 0,
          '& .MuiListItem-root:not(:last-child)': {
            mb: 0.5,
          },
          mb: 0,
        }}
      >
        {isFreePlan && hasBusinessGoal && (
          <ListItem disablePadding sx={{ display: 'block' }}>
            <Tooltip
              title={open ? 'Schedule a Call with Sales' : 'Contact Sales'}
              placement="right"
              arrow
            >
              <ListItemButton
                onClick={() =>
                  window.open('https://forms.gle/44uJ7NSs7CKmL6QW7', '_blank')
                }
                sx={{
                  justifyContent: open ? 'initial' : 'center',
                  px: open ? 2.5 : 1.25,
                  borderRadius: 2,
                  mx: 0.5,
                  minHeight: '36px',
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    '& .MuiSvgIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                  mb: 0.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    pl: open ? 0 : 0.5,
                  }}
                >
                  <HelpCircle
                    sx={{
                      color: 'primary.contrastText',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Contact Sales"
                  sx={{
                    opacity: open ? 1 : 0,
                    '& .MuiTypography-root': {
                      color: 'inherit',
                    },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {isTrialAvailable && (
          <ListItem disablePadding sx={{ display: 'block' }}>
            <Tooltip
              title={open ? 'Start 3-Day Free Trial' : 'Free Trial'}
              placement="right"
              arrow
            >
              <ListItemButton
                onClick={() => setShowTrialModal(true)}
                sx={{
                  justifyContent: open ? 'initial' : 'center',
                  px: open ? 2.5 : 1.25,
                  borderRadius: 2,
                  mx: 0.5,
                  minHeight: '36px',
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    '& .MuiSvgIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                  mb: 0.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    pl: open ? 0 : 0.5,
                  }}
                >
                  <Calendar
                    sx={{
                      color: 'primary.contrastText',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Free Trial"
                  sx={{
                    opacity: open ? 1 : 0,
                    '& .MuiTypography-root': {
                      color: 'inherit',
                    },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {actions.map(({ key, label, startIcon, href, tooltip }) => (
          <ListItem key={key} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={open ? tooltip : label} placement="right" arrow>
              <ListItemButton
                component={Link}
                to={href || ''}
                onClick={() => handleQuickActionClick()}
                sx={{
                  justifyContent: open ? 'initial' : 'center',
                  px: open ? 2.5 : 1.25,
                  borderRadius: 2,
                  mx: 0.5,
                  minHeight: '36px',
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: 'transparent',
                  '&.Mui-selected': {
                    bgcolor: 'transparent',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    backgroundColor:
                      folderId === key ? 'secondary.main' : 'secondary.light',
                    opacity: folderId === key ? 1 : 0,
                    borderRadius: 'inherit',
                  },
                  '&:hover': {
                    '&::before': {
                      opacity: 1,
                    },
                  },
                  '& > *': {
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    pl: open ? 0 : 0.5,
                  }}
                >
                  {startIcon}
                </ListItemIcon>
                <ListItemText primary={label} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}

        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title="Ask AI" placement="right" arrow>
            <ListItemButton
              onClick={() => {
                const newState = !isAskAIOpen;
                setIsAskAIOpen(newState);
                setShowMultiAskAI(newState);
                if (isMobile && newState) {
                  dispatch(setDrawerState('closed'));
                }
              }}
              sx={{
                justifyContent: open ? 'initial' : 'center',
                px: open ? 2.5 : 1.25,
                borderRadius: 2,
                mx: 0.5,
                minHeight: '36px',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'transparent',
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  backgroundColor: 'secondary.main',
                  opacity: isAskAIOpen ? 1 : 0,
                  borderRadius: 'inherit',
                },
                '&:hover': {
                  '&::before': {
                    opacity: 1,
                  },
                },
                '& > *': {
                  position: 'relative',
                  zIndex: 1,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  pl: open ? 0 : 0.5,
                }}
              >
                {isAskAIOpen ? <X /> : <Search />}
              </ListItemIcon>
              <ListItemText
                primary="Ask AI"
                sx={{
                  opacity: open ? 1 : 0,
                  '& .MuiTypography-root': {
                    fontWeight: isAskAIOpen ? 700 : 400,
                  },
                }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      {showTrialModal && <FreeTrial onClose={() => setShowTrialModal(false)} />}
    </>
  );
};

export default DrawerActions;
